import { fitToRenderArea } from 'features/layout/fitToRenderArea';
import { getTileDisplaySize } from 'features/layout/getTileDisplaySize';
import { layoutConfig } from 'utils/layout';

// Granularity of the search below. Smaller = more precise minimum, more iterations.
const MIN_CONTENT_WIDTH_SEARCH_STEP = 0.01;

/**
 * Finds the smallest content-area width fraction (0..1) at which the pinned content tile is
 * still at least as large as a single regular tile in BOTH dimensions.
 *
 * Widening the content grows the content tile and shrinks the area left for the regular tiles,
 * so a crossover always exists. We simulate the regular-tile size with `fitToRenderArea` (the same
 * math the grid uses) for each candidate fraction and return the first one where the content wins.
 * This prevents the pinned tile from rendering smaller than the surrounding tiles (SAMBA-9066).
 */
export const getMinContentWidth = ({
  roomWidth,
  roomHeight,
  tileCount,
  contentRatio,
  deviceType,
}: {
  roomWidth: number;
  roomHeight: number;
  tileCount: number;
  contentRatio: number;
  deviceType: string;
}) => {
  const { minContentWidth, maxContentWidth } = layoutConfig;

  if (!roomWidth || !roomHeight || tileCount <= 0) {
    return minContentWidth;
  }

  for (
    let fraction = minContentWidth;
    fraction < maxContentWidth;
    fraction += MIN_CONTENT_WIDTH_SEARCH_STEP
  ) {
    const contentWidth = fraction * roomWidth;
    // Content keeps its own aspect ratio, so its height is letterboxed within the column.
    const contentHeight = Math.min(roomHeight, contentWidth * contentRatio);

    const tilesWidth = roomWidth - contentWidth;
    const { tileWidth, tileHeight } = fitToRenderArea(
      tilesWidth,
      roomHeight,
      tileCount,
      deviceType
    );

    // Compare against the size a tile is actually painted at, not the raw grid cell. A single tile in
    // a tall strip gets a full-height cell, but it renders capped (padded); comparing content against
    // the uncapped cell height never crosses over, forcing content to maxContentWidth (SAMBA-9207).
    const { displayWidth, displayHeight } = getTileDisplaySize(tileWidth, tileHeight);

    if (contentWidth >= displayWidth && contentHeight >= displayHeight) {
      return fraction;
    }
  }

  return maxContentWidth;
};
